// Shared row behavior for the BatchView data table and the
// BatchItemAlternateStores (cascaded store) data table. Keeping these helpers
// in one place keeps the focal rows and alternate-store rows behaving
// identically and guards against the two tables drifting apart.
import Rules from '@/axios/rules-management'

// The share of the deal a store must pass through to the consumer when it has no
// min_pass_thru_pct rule of its own (the rule template's default). Used only for wording
// until the API returns each row's own share as min_pass_thru_pct.
export const DEFAULT_MIN_PASS_THROUGH_PCT = 70

export const MULTIPLIER_MESSAGE = 'Multiplier must be a whole number of 1 or more.'

export const tprItemRow = {
  computed: {
    srpRules() {
      const rules = []
      const valueRequired = v => !!v || 'SRP Required'
      rules.push(valueRequired)
      const positiveValue = v => parseFloat(v) >= 0 || 'Invalid SRP'
      rules.push(positiveValue)
      return rules
    },
    multiplierRules() {
      const rules = []
      const valueRequired = v => !!v || 'Multiplier Required'
      rules.push(valueRequired)
      const wholeNumber = v => this.isValidMultiplier(v) || MULTIPLIER_MESSAGE
      rules.push(wholeNumber)
      return rules
    },
    blockNonCompliantSaves() {
      return this.tprProTenants && !this.isTprProRetailUser
    },
    complianceBatch() {
      return this.selectedBatch
    }
  },
  methods: {
    // An excluded row still receives a propagated price -- it costs no extra logic and the
    // row is not going to a shelf -- but it must not be *judged*. The compliance rules exist
    // to keep a bad price off a tag, and an excluded row produces no tag, so refusing a whole
    // save over one would block a price nobody is setting. A blank or absent reason means
    // included: the engine writes '' for a live row, and a row built without the field is
    // not excluded.
    rowIsExcluded(row) {
      return row?.excluded != null && row.excluded !== ''
    },
    // A multiplier is a unit count, so it must be a whole number of 1 or more. Both APIs store it as an
    // integer: the ad-group save truncates 0.5 to 0 and 2.5 to 2, and the store save rejects it.
    isValidMultiplier(value) {
      if (value === '' || value === null || value === undefined) return false
      const multiplier = Number(value)
      return Number.isInteger(multiplier) && multiplier >= 1
    },
    srpExceedsBaseRetail(item) {
      // AWG does not maintain perishable base retails reliably, so the base-retail ceiling
      // is not trustworthy for perishable batches — exempt them from this gate.
      if (String(this.complianceBatch?.batch_type || '').toUpperCase() === 'PERISHABLE') return false
      const multiplier = Number(item.multiplier) || 1
      if (multiplier <= 0) return false
      const retail = Number(item.retail_price)
      const promo = Number(item.promotion_price)
      if (!(retail > 0) || !(promo > 0)) return false
      return (promo / multiplier) > retail
    },
    // Per-unit required retail on a compliance-enforced TPR: the price its ePay allowance is tied to,
    // the original promo price (the group price, for a group manager). This is the price the
    // retailer sees on the promo, so it is the one the messages name. NaN when there is none.
    requiredRetail(item) {
      const base = parseFloat(this.isTprProRetailUser ? item.original_promotion_price : item.rules_promotion_price)
      return base > 0 ? base : NaN
    },
    // How far above the required retail an SRP can go and keep the allowance: the promo's variance.
    // item.variance is blank on store-group rows; the backend defaults it to the nickel tolerance, so mirror that.
    complianceTolerance(item) {
      const variance = parseFloat(item.variance)
      return Number.isNaN(variance) ? 0.05 : variance
    },
    // The most the SRP, for its multiplier, can be and keep the allowance. NaN when there is no
    // required retail, which no SRP exceeds.
    srpComplianceLimit(item) {
      // toFixed: 5.89 + 0.05 is 5.9399... in floating point, which would flag $5.94
      return Number((this.requiredRetail(item) + this.complianceTolerance(item)).toFixed(2)) * item.multiplier
    },
    // "more than $0.05 above the $3.99 required retail", priced for the SRP's multiplier ("2/$7.98").
    requiredRetailPhrase(item) {
      const multiplier = Number(item.multiplier) || 1
      const price = `${multiplier > 1 ? `${multiplier}/` : ''}$${(this.requiredRetail(item) * multiplier).toFixed(2)}`
      const tolerance = this.complianceTolerance(item) * multiplier
      return `${tolerance > 0 ? `more than $${tolerance.toFixed(2)} ` : ''}above the ${price} required retail`
    },
    // Above the required retail a retailer only gives up the ePay allowance, so this warns (the SRP
    // field and the compliance icon turn red, and a message floats up) but does not block their
    // save. It does block a group manager's, behind blockNonCompliantSaves.
    srpIsCompliant(item) {
      if (!item.enforce_compliance) return true
      return !(parseFloat(item.promotion_price) > this.srpComplianceLimit(item))
    },
    complianceIconColor(item) {
      if (!item.enforce_compliance) return 'grey lighten-1'
      return this.tprProTenants && !this.srpIsCompliant(item) ? this.getWarningColor(item) : 'green'
    },
    // The mandatory pass-through: the store must pass at least its minimum share of the deal
    // through to the consumer, so the per-unit SRP may not be above max_srp_for_passthrough_percent.
    // This is the only limit that blocks a retailer's save, and it applies to every row,
    // compliance-enforced ones included. Lived on BatchView only, so cascaded rows were never
    // checked against it at all -- the child table could save any SRP without a word.
    // `selectedBatch` is a prop on the cascaded table and data on BatchView; both resolve here.
    passPercentIsCompliant(item) {
      const nonPerishable = String(this.complianceBatch?.batch_type || '').toUpperCase() === 'NON PERISHABLE'
      if (!(this.$auth.tenant === 'alliance-retail-group' && this.isTprProRetailUser && item.retail_price != 0 && nonPerishable)) return true
      const maxSRP = this.passPercentMaxSRP(item)
      if (maxSRP <= 0) return true
      return (parseFloat(item.promotion_price) / item.multiplier) <= maxSRP
    },
    // Per-unit pass-through ceiling. The required retail is not a ceiling: going above it costs
    // the ePay allowance, which is the retailer's call (see srpIsCompliant).
    // This picks the ceiling only; it does not decide whether a row is judged at all. That
    // scope -- ARG, retail user, Non-Perishable, a base retail -- is passPercentIsCompliant's,
    // and every caller goes through it first, so call that rather than this.
    passPercentMaxSRP(item) {
      return parseFloat(item.max_srp_for_passthrough_percent) || 0
    },
    // The row's store's minimum share of the deal, as a percentage.
    passThroughPct(item) {
      const pct = parseFloat(item?.min_pass_thru_pct)
      return Number.isNaN(pct) ? DEFAULT_MIN_PASS_THROUGH_PCT : pct
    },
    // "at least 70% of the deal" for rows whose stores all hold the same share.
    passThroughShareFor(rows) {
      const pcts = [...new Set(rows.map(row => this.passThroughPct(row)))]
      return pcts.length === 1 ? `at least ${pcts[0]}% of the deal` : 'the minimum share of the deal'
    },
    passThroughMessage(item) {
      const maxSRP = this.passPercentMaxSRP(item) * (Number(item.multiplier) || 1)
      return `At least ${this.passThroughPct(item)}% of the deal must be passed through to the consumer. ` +
        `Item ${item.item_id} must have an SRP at or below $${maxSRP.toFixed(2)}.`
    },
    // Hover text on an SRP field above the required retail, naming the promos whose allowance
    // it gives up (nonCompliantList is filled in as the row's net GP is scored).
    srpComplianceTooltip(item) {
      const promos = Array.isArray(item.nonCompliantList) && item.nonCompliantList.length
        ? ` This store gives up the allowance on: ${item.nonCompliantList.join(', ')}.`
        : ''
      return `SRP is ${this.requiredRetailPhrase(item)} for the ePay allowance.${promos}`
    },
    requiredRetailMessage(item) {
      return `Item ${item.item_id}: the SRP is ${this.requiredRetailPhrase(item)} ` +
        'for the ePay allowance. You can still save it, but this store will give up the allowance.'
    },
    // The one floating message for an SRP or multiplier the user just left. The pass-through
    // blocks the save, so its message wins over the ePay warning, which does not. An excluded
    // row puts no price on a tag, so it gives up no allowance and gets no ePay warning.
    warnOnSrpLimits(item) {
      if (!item || !this.isTprProRetailUser) return
      if (!this.passPercentIsCompliant(item)) {
        if (this.passPercentMaxSRP(item) > 0) this.alert(this.passThroughMessage(item))
        return
      }
      if (!this.rowIsExcluded(item) && !this.srpIsCompliant(item)) this.alert(this.requiredRetailMessage(item))
    },
    // Trim to 2 decimals without rounding, so an SRP is never nudged up a cent. This lived
    // on BatchView, which meant the cascaded table never trimmed at all and could store a
    // sub-cent price that only looked clean because the reload formatter rounds it.
    truncateToTwoDecimals(value) {
      if (value === '' || value === null || value === undefined) {
        return value
      }
      const stringValue = String(value)
      const decimalIndex = stringValue.indexOf('.')
      const truncated = decimalIndex > -1 ? 
        stringValue.substring(0, decimalIndex + 3) 
        : stringValue
      return parseFloat(truncated).toFixed(2)
    },
    // Lazily load per-promo compliance data so both callers fetch it identically.
    async ensureEnforcedPromos(item, batchId, storeId) {
      if (this.isTprProRetailUser && item.enforce_compliance && item.enforce_compliance_promos == undefined) {
        const res = await Rules.getEnforcedPromos(batchId, storeId, item.item_id)
        item.enforce_compliance_promos = res.data
      }
    },
    // Single source of truth for Net GP%; caller passes srp/multiplier and pre-loads promos.
    calculateNetGrossProfit(item, srp, multiplier, isPerishable = false) {
      if (multiplier <= 0 || srp <= 0) {
        return 0.00
      }
      let allowances = Number(item.amap_allowance) + Number(item.dsd_oi_allowance) + Number(item.billback_allowance) + Number(item.tpr_billback_allowance) + Number(item.edlc_billback_allowance) + Number(item.vpap_tpr_billback_allowance) + Number(item.accrual_tpr_billback_allowance)
      // Exclude Early Buy Allowance for ARG non-perishable batches only
      if (this.$auth.tenant != 'alliance-retail-group' || isPerishable) {
        allowances += Number(item.early_buy_allowance)
      }
      let scanAllowances = Number(item.scan_allowance) + Number(item.edlc_scan_allowance) + Number(item.tpr_scan_allowance)
      const nonCompliantList = []
      const catToCol = {
        "TPR Scan - ePay": "tpr_scan_epay_allowance",
        "TPR Scan - Compete": "tpr_scan_compete_allowance",
        "EDLC Scan - ePay": "edlc_scan_epay_allowance",
        "EDLC Scan - Compete": "edlc_scan_compete_allowance"
      }
      if (this.isTprProRetailUser) {
        item.enforce_compliance_promos?.forEach((promo) => {
          const col = catToCol[promo.promo_category_name]
          // per-unit price vs the per-promo threshold + variance (item.variance is blank on store-group rows)
          if ((srp / multiplier) <= (parseFloat(promo.promotion_price) + (parseFloat(promo.variance ?? item.variance) || 0))) {
            scanAllowances += item[col] ?? 0
          } else {
            nonCompliantList.push(promo.promo_category_name)
          }
        })
        this.$set(item, 'nonCompliantList', nonCompliantList)
      } else {
        scanAllowances += Number(item.tpr_scan_compete_allowance) + Number(item.tpr_scan_epay_allowance) + Number(item.edlc_scan_compete_allowance) + Number(item.edlc_scan_epay_allowance)
      }
      let cost_source = item.cost
      if (this.tprProTenants && item.case_freight != null) {
        cost_source = item.case_freight
      }
      const unitCost = ((Number(cost_source) - allowances) / Number(item.qty_per_case)) - scanAllowances
      item.net_unit_cost = Math.round(unitCost * 100) / 100
      const grossProfit = (((Number(srp) / Number(multiplier)) - unitCost) / (Number(srp) / Number(multiplier))) * 10000
      return Math.round(grossProfit) / 100
    },
    findMinDiscountRule(item) {
      if (!this.hasRules(item.rules_applied)) return null
      let rules
      try {
        rules = typeof item.rules_applied === 'string' ? JSON.parse(item.rules_applied) : item.rules_applied
      } catch (err) {
        return null
      }
      if (!Array.isArray(rules)) return null
      const rule = rules.find(r => r && r.name && r.name.toLowerCase().includes('discount'))
      if (!rule) return null
      const name = String(rule.name || '').toLowerCase()
      const label = String(rule.label || '').toLowerCase()
      const isPercent = name.includes('pct') || name.includes('percent') || label.includes('percent') || label.includes('%')
      return { rule, isPercent }
    },
    getExclusionReason(item) {
      if (!item) return ''
      const included = item.excluded === '' || item.excluded === null
      if (included) return ''

      // Mirror primary's reason on cascaded rows; generic note otherwise.
      if (item.exclusion_reason) return item.exclusion_reason

      // This is not implemented as a rule, so have to check for seperately outside of below exclusion value check
      if (this.srpExceedsBaseRetail(item)) return 'TPR SRP exceeds Base Retail'

      // When rules are applied, an exclusion string for the exclusion reason is added to the item's data. Use that as a base
      //  to return a more detailed string for the tooltip
      switch (item.excluded) {
        case 'minimum_discount':
          return 'Excluded by Min Discount $ Rule'

        case 'minimum_percentage':
          return 'Excluded by Min Discount % Rule'

        case 'manual':
          return 'Un-included'

        case 'promotion_price':
          return 'Promotion Price is not valid'

        case 'invalid_retail':
          return 'Retail price is not valid'

        case 'invalid_quantity':
          return 'Case quantity is not valid'

        case 'invalid_srp':
          return 'TPR SRP is not valid'

        default:
          return item.excluded
      }
    },
    hasRules(rulesApplied) {
      if (!rulesApplied || rulesApplied === '[]') return false
      try {
        const parsed = JSON.parse(rulesApplied)
        return Array.isArray(parsed) && parsed.length > 0
      } catch (err) {
        console.error('Invalid JSON in rules_applied:', err)
        return false
      }
    },
    parseRules(rulesApplied) {
      // Friendly labels for engine marker pseudo-rules.
      const markerLabels = { 'Non-Subscriber': 'Not a Price Pro store' }
      try {
        const parsedRules = typeof rulesApplied === 'string' ? JSON.parse(rulesApplied) : rulesApplied
        return parsedRules.map(rule => {
          if (rule.name && rule.name.toLowerCase().includes('discount')) {
            rule.displayValue = `$${rule.value}`
          } else if (rule.name && /pct|percent/.test(rule.name.toLowerCase())) {
            rule.displayValue = `${rule.value}%`
          } else {
            rule.displayValue = rule.value
          }
          rule.displayLabel = markerLabels[rule.name] || rule.display_name || rule.name
          return rule
        })
      } catch (err) {
        console.error('Failed to parse rules_applied:', err)
        return []
      }
    },
    getRuleDisplay(ruleKey) {
      const ruleMapping = {
        'ad_group': 'Ad Group',
        'store': 'Store',
        'department': 'Department',
        'category': 'Category',
        'vendor': 'Vendor'
      }
      return ruleMapping[ruleKey] || ruleKey
    },
    getWarningColor(item) {
      if (this.changes.some(change => change.id === item.id) && this.blockNonCompliantSaves) {
        return 'white'
      }
      return 'red'
    },
    getCompetitorPrice(item, competitor) {
      const value = item[`comp_pricing_${competitor}`]
      return value !== undefined ? value : 'N/A'
    },
    getPriceComparisonColor(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'grey'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return 'grey'
      if (userprice > compprice) return 'red'
      return 'green'
    },
    getPriceComparisonIcon(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'mdi-minus'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return 'mdi-equal'
      if (userprice > compprice) return 'mdi-arrow-up'
      return 'mdi-arrow-down'
    },
    getPriceComparisonText(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'No price comparison available'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return `Prices are equal to $${compprice}`
      if (userprice > compprice) {
        const diff = ((userprice - compprice) / compprice * 100).toFixed(2)
        return `Price ${diff}% More Expensive than Competitor price of $${compprice}`
      }
      const diff = ((compprice - userprice) / compprice * 100).toFixed(2)
      return `Price ${diff}% Cheaper than Competitor price of $${compprice}`
    }
  }
}
